import { useState } from "react";
import { motion } from "framer-motion";
import { fadeInUp, fadeInRight, viewportConfig } from "@/lib/motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, MapPin, Phone, Mail } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  "BESS Solutions",
  "EV Battery Packs",
  "Industrial / Custom Solutions",
  "Partnership Inquiry",
  "General Inquiry",
];

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Inquiry submitted. Our team will respond within 24 hours.");
      setForm({ name: "", email: "", company: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-10 md:py-14 lg:py-20 bg-[#040D1C] noise-overlay">
      <div className="section-glow absolute inset-0 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportConfig} variants={fadeInUp} className="mb-4">
          <span className="text-xs uppercase tracking-[0.3em] text-[#1E96FC]">
            // Contact
          </span>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={viewportConfig} variants={fadeInUp} className="mb-10 md:mb-16">
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-slate-100 leading-tight max-w-3xl">
            Start a <span className="text-[#1E96FC]">conversation</span>
          </h2>
        </motion.div>

        <div className="glass-card rounded-sm p-6 md:p-10 lg:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          {/* Form */}
          <motion.div initial="hidden" whileInView="visible" viewport={viewportConfig} variants={fadeInUp} className="md:col-span-7 flex">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5 flex flex-col w-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs uppercase tracking-wider text-slate-400">Name *</Label>
                  <Input data-testid="contact-name" id="name" name="name" value={form.name} onChange={handleChange} placeholder="Full Name" className="bg-[#040D1C] border-[#003771]/40 text-slate-100 focus:border-[#1E96FC] rounded-sm h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-400">Email *</Label>
                  <Input data-testid="contact-email" id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="work@company.com" className="bg-[#040D1C] border-[#003771]/40 text-slate-100 focus:border-[#1E96FC] rounded-sm h-11" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-xs uppercase tracking-wider text-slate-400">Company</Label>
                  <Input data-testid="contact-company" id="company" name="company" value={form.company} onChange={handleChange} placeholder="Organization" className="bg-[#040D1C] border-[#003771]/40 text-slate-100 focus:border-[#1E96FC] rounded-sm h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-slate-400">Phone</Label>
                  <Input data-testid="contact-phone" id="phone" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX" className="bg-[#040D1C] border-[#003771]/40 text-slate-100 focus:border-[#1E96FC] rounded-sm h-11" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject" className="text-xs uppercase tracking-wider text-slate-400">Subject *</Label>
                <Select
                  value={form.subject}
                  onValueChange={(val) => setForm({ ...form, subject: val })}
                >
                  <SelectTrigger data-testid="contact-subject" className="bg-[#040D1C] border-[#003771]/40 text-slate-100 focus:border-[#1E96FC] rounded-sm h-11">
                    <SelectValue placeholder="Select inquiry type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#040D1C] border-[#003771]/40">
                    {SUBJECTS.map((s) => (
                      <SelectItem key={s} value={s} className="text-slate-300 focus:bg-[#003771]/30 focus:text-[#1E96FC]">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 flex-1 flex flex-col">
                <Label htmlFor="message" className="text-xs uppercase tracking-wider text-slate-400">Message *</Label>
                <Textarea data-testid="contact-message" id="message" name="message" value={form.message} onChange={handleChange} placeholder="Describe your requirements, project scale, and timeline..." className="bg-[#040D1C] border-[#003771]/40 text-slate-100 focus:border-[#1E96FC] rounded-sm resize-none flex-1 min-h-[120px]" />
              </div>
              <Button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={loading}
                className="bg-[#1E96FC] text-white hover:bg-[#1E96FC]/90 rounded-sm px-6 md:px-8 py-5 md:py-6 text-xs md:text-sm uppercase tracking-[0.15em] font-bold transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 w-full sm:w-auto justify-center min-h-[48px]"
              >
                {loading ? "Sending..." : "Submit Inquiry"}
                <Send className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </motion.div>

          {/* Info */}
          <motion.div initial="hidden" whileInView="visible" viewport={viewportConfig} variants={fadeInRight} className="md:col-span-5 flex">
            <div className="rounded-sm space-y-6 md:space-y-8 w-full md:border-l md:border-[#003771]/40 md:pl-8 lg:pl-10">
              <div>
                <h4 className="text-lg font-bold uppercase text-slate-200 mb-4 tracking-wide">
                  Reach Us
                </h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-[#1E96FC] mt-1 flex-shrink-0" />
                    <p className="text-sm text-slate-400">Sharda Energy Tech Pvt. Ltd., Industrial Area, India</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-[#1E96FC] flex-shrink-0" />
                    <p className="text-sm text-slate-400">+91 XXXXX XXXXX</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-[#1E96FC] flex-shrink-0" />
                    <p className="text-sm text-slate-400">info@shardaenertech.com</p>
                  </div>
                </div>
              </div>

              <div className="h-px bg-[#003771]/30" />

              <div>
                <h4 className="text-base font-bold uppercase text-slate-200 mb-3 tracking-wide">
                  Response Timeline
                </h4>
                <p className="text-sm text-slate-400 leading-relaxed">
                  Our business development team reviews every inquiry and responds
                  within 24 business hours. Investor, partnership, and pre-order
                  interest gets priority routing.
                </p>
              </div>

              <div className="h-px bg-[#003771]/30" />

              <div>
                <h4 className="text-base font-bold uppercase text-slate-200 mb-3 tracking-wide">
                  We're Building For
                </h4>
                <div className="flex flex-wrap gap-2">
                  {["OEMs", "Fleet Operators", "Utilities", "Government", "System Integrators", "EPCs"].map((tag) => (
                    <span key={tag} className="px-3 py-1.5 bg-[#003771]/30 text-xs text-slate-300 uppercase tracking-wider rounded-sm border border-[#003771]/50">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
